import { computed, onScopeDispose, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { normalizeLocale, type AppLocale } from '@/i18n/locales'
import { normalizePipSkin, normalizePipStreamOptions, type PipSkin } from '@/ui/pip-skins'
import { normalizePerformanceSettings, performanceConfigs } from '@/core/performance-settings'
import { readLocalPreference, writeLocalPreference } from '@/utils/local-preference'
import { normalizeRateUnit, normalizeLevelEtaMode, normalizeOcrRecognitionModel } from '@/core/display-settings'
import type { PipWidgetMode } from '@/services/pip-availability'

export type ThemePreference = 'light' | 'dark'

const PIP_SKIN_MIGRATION_KEY = 'maple-log:pip-skin-migration-v1'

function initialPipSkin(): PipSkin {
  const stored = readLocalPreference('maple-log:pip-skin')
  if (readLocalPreference(PIP_SKIN_MIGRATION_KEY) === '1') return normalizePipSkin(stored)
  const skin = normalizePipSkin(stored === 'default' ? 'daily-order' : stored)
  try {
    // Save the migrated choice before marking completion, so a failed write can retry.
    if (stored === 'default') localStorage.setItem('maple-log:pip-skin', skin)
    localStorage.setItem(PIP_SKIN_MIGRATION_KEY, '1')
  } catch { /* Use the migrated choice in memory when storage is unavailable. */ }
  return skin
}

function initialTheme(): ThemePreference {
  const stored = readLocalPreference('maple-log:theme')
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function initialStreamOptions() {
  try { return normalizePipStreamOptions(JSON.parse(localStorage.getItem('maple-log:pip-stream') ?? 'null')) }
  catch { return normalizePipStreamOptions(null) }
}

function initialPerformanceSettings() {
  try { return normalizePerformanceSettings(JSON.parse(localStorage.getItem('maple-log:performance') ?? 'null')) }
  catch { return normalizePerformanceSettings(null) }
}

export const useAppStore = defineStore('app', () => {
  const locale = ref<AppLocale>(normalizeLocale(readLocalPreference('maple-log:locale')))
  const theme = ref<ThemePreference>(initialTheme())
  const pipSkin = ref<PipSkin>(initialPipSkin())
  const pipWindowMode = ref<PipWidgetMode>(readLocalPreference('maple-log:pip-window-mode') === 'popup' ? 'popup' : 'pip')
  const pipStream = ref(initialStreamOptions())
  const performanceSettings = ref(initialPerformanceSettings())
  const performanceConfig = computed(() => performanceConfigs[performanceSettings.value.tier])
  const presetId = ref(readLocalPreference('maple-log:preset') ?? 'mxdc')
  // Old manual choices must never disable automatic game detection.
  try { localStorage.removeItem('maple-log:preset-selection') } catch { /* storage may be unavailable */ }
  const rateUnit = ref(normalizeRateUnit(readLocalPreference('maple-log:rate-unit')))
  const levelEtaMode = ref(normalizeLevelEtaMode(readLocalPreference('maple-log:level-eta-mode')))
  const ocrRecognitionModel = ref(normalizeOcrRecognitionModel(readLocalPreference('maple-log:ocr-recognition-model')))
  const resolvedTheme = computed(() => theme.value)

  watch(locale, (value) => writeLocalPreference('maple-log:locale', value))
  watch(theme, (value) => writeLocalPreference('maple-log:theme', value))
  watch(pipSkin, (value) => writeLocalPreference('maple-log:pip-skin', value))
  watch(pipWindowMode, value => writeLocalPreference('maple-log:pip-window-mode', value))
  watch(pipStream, (value) => writeLocalPreference('maple-log:pip-stream', JSON.stringify(value)), { deep: true })
  watch(performanceSettings, (value) => {
    try { localStorage.setItem('maple-log:performance', JSON.stringify(normalizePerformanceSettings(value))) } catch { /* memory-only when storage is unavailable */ }
  }, { deep: true, immediate: true })
  watch(presetId, (value) => writeLocalPreference('maple-log:preset', value))
  watch(rateUnit, value => writeLocalPreference('maple-log:rate-unit', value))
  watch(levelEtaMode, value => writeLocalPreference('maple-log:level-eta-mode', value))
  watch(ocrRecognitionModel, value => writeLocalPreference('maple-log:ocr-recognition-model', value))
  watch(resolvedTheme, (value) => {
    document.documentElement.dataset.theme = value
    document.documentElement.style.colorScheme = value
  }, { immediate: true })

  // Keep a recording tab's overlay in sync with settings changed in another tab.
  const syncWidgetSettings = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return
    if (event.key === 'maple-log:pip-skin') pipSkin.value = normalizePipSkin(event.newValue)
    if (event.key === 'maple-log:pip-window-mode') pipWindowMode.value = event.newValue === 'popup' ? 'popup' : 'pip'
    if (event.key === 'maple-log:pip-stream') pipStream.value = initialStreamOptions()
    if (event.key === 'maple-log:performance') performanceSettings.value = initialPerformanceSettings()
    if (event.key === 'maple-log:rate-unit') rateUnit.value = normalizeRateUnit(event.newValue)
    if (event.key === 'maple-log:level-eta-mode') levelEtaMode.value = normalizeLevelEtaMode(event.newValue)
    if (event.key === 'maple-log:locale') locale.value = normalizeLocale(event.newValue)
    if (event.key === 'maple-log:theme' && (event.newValue === 'light' || event.newValue === 'dark')) theme.value = event.newValue
  }
  window.addEventListener('storage', syncWidgetSettings)
  onScopeDispose(() => window.removeEventListener('storage', syncWidgetSettings))

  function toggleTheme(): void {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  return { locale, theme, pipSkin, pipWindowMode, pipStream, performanceSettings, performanceConfig, presetId, rateUnit, levelEtaMode, ocrRecognitionModel, resolvedTheme, toggleTheme }
})
